import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { TrendChart, BarChartComponent, PieChartComponent } from '../../components/dashboard/Charts';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDateRange, formatNumber, formatCurrency, calculatePercentage } from '../../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
    ArrowLeft, Linkedin, Mail, Megaphone, TrendingUp,
    Target, Users, Send, UserCheck, MessageSquare, ThumbsUp, ThumbsDown, Clock, Award,
    Eye, MousePointer, Inbox, Heart, FileText,
    Percent, DollarSign, Building, Briefcase,
    Lightbulb, AlertTriangle, CheckCircle, FileDown, Presentation
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PptxGenJS from 'pptxgenjs';
import { formatDate } from '../../lib/utils';

export default function ReportDetailPage() {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [exporting, setExporting] = useState(null);

    useEffect(() => {
        loadReport();
    }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadReport = async () => {
        try {
            const response = await reportsApi.getAll();
            const found = response.data.find(r => r.id === reportId);
            if (found) {
                setReport(found);
            } else {
                toast.error('Report not found');
                navigate('/reports/weekly');
            }
        } catch (error) {
            toast.error('Failed to load report');
        } finally {
            setLoading(false);
        }
    };

    const exportToPDF = async () => {
        if (!report) return;
        setExporting('pdf');
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pw = pdf.internal.pageSize.getWidth();
            const red = [220, 38, 38], dark = [15, 15, 15], white = [255, 255, 255], muted = [161, 161, 170];

            pdf.setFillColor(...dark);
            pdf.rect(0, 0, pw, 35, 'F');
            pdf.setTextColor(...red); pdf.setFontSize(22); pdf.setFont('helvetica', 'bold');
            pdf.text('NOVANTPRO', 15, 22);
            pdf.setTextColor(...white); pdf.setFontSize(10);
            pdf.text(`${report.report_type.toUpperCase()} REPORT`, pw - 50, 15);
            pdf.setTextColor(...muted); pdf.setFontSize(8);
            pdf.text(formatDateRange(report.period_start, report.period_end), pw - 50, 23);

            let y = 45;
            pdf.setTextColor(...red); pdf.setFontSize(13); pdf.setFont('helvetica', 'bold');
            pdf.text('KEY METRICS', 15, y); y += 3;
            autoTable(pdf, {
                startY: y,
                head: [['Metric', 'Value']],
                body: Object.entries(report.metrics).map(([k, v]) => [k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), String(v)]),
                theme: 'grid', headStyles: { fillColor: red, textColor: white, fontSize: 9 },
                bodyStyles: { fontSize: 9 }, alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 15, right: 15 },
            });
            y = pdf.lastAutoTable.finalY + 12;

            pdf.setTextColor(...red); pdf.setFontSize(13); pdf.text('CHANNEL PERFORMANCE', 15, y); y += 3;
            autoTable(pdf, {
                startY: y,
                head: [['Channel', 'Metric 1', 'Metric 2', 'Metric 3', 'MQLs']],
                body: [
                    ['LinkedIn', `Reachouts: ${report.linkedin.reachouts_sent}`, `Accepted: ${report.linkedin.connection_accepted}`, `Responses: ${report.linkedin.responses}`, String(report.linkedin.mqls)],
                    ['Email', `Sent: ${report.email.emails_sent}`, `Opened: ${report.email.opened}`, `Clicked: ${report.email.clicked}`, String(report.email.mqls)],
                    ['Ads', `Impressions: ${formatNumber(report.ads.impressions)}`, `Clicks: ${report.ads.clicks}`, `CTR: ${(report.ads.ctr || 0).toFixed(2)}%`, '-'],
                    ['Inbound', `Views: ${report.inbound.total_views}`, `Engagement: ${report.inbound.total_engagement}`, `Leads: ${report.inbound.total_leads}`, '-'],
                ],
                theme: 'grid', headStyles: { fillColor: red, textColor: white, fontSize: 8 },
                bodyStyles: { fontSize: 8 }, alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 15, right: 15 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 25 } },
            });

            pdf.save(`NovantPro-${report.report_type}-${formatDate(report.period_start)}.pdf`);
            toast.success('PDF exported');
        } catch (e) { toast.error('PDF export failed'); }
        finally { setExporting(null); }
    };

    const exportToPPT = async () => {
        if (!report) return;
        setExporting('ppt');
        try {
            const pptx = new PptxGenJS();
            pptx.layout = 'LAYOUT_WIDE';
            const R = 'DC2626', D = '0A0A0A', C = '191C24', W = 'FFFFFF', M = 'A1A1AA';

            const s1 = pptx.addSlide(); s1.background = { color: D };
            s1.addText('NOVANTPRO', { x: 0.8, y: 1.5, w: 6, h: 1.2, fontSize: 44, color: R, bold: true });
            s1.addText(`${report.report_type.toUpperCase()} REPORT`, { x: 0.8, y: 2.7, w: 6, h: 0.8, fontSize: 28, color: W, bold: true });
            s1.addText(formatDateRange(report.period_start, report.period_end), { x: 0.8, y: 3.5, w: 6, h: 0.5, fontSize: 16, color: M });

            const s2 = pptx.addSlide(); s2.background = { color: D };
            s2.addText('KEY METRICS', { x: 0.5, y: 0.3, w: 5, h: 0.6, fontSize: 24, color: R, bold: true });
            Object.entries(report.metrics).forEach(([k, v], i) => {
                const col = i % 3, row = Math.floor(i / 3);
                s2.addShape(pptx.ShapeType.roundRect, { x: 0.5 + col * 4, y: 1.2 + row * 1.5, w: 3.6, h: 1.2, fill: { color: C }, rectRadius: 0.1 });
                s2.addText(k.replace(/_/g, ' ').toUpperCase(), { x: 0.7 + col * 4, y: 1.35 + row * 1.5, w: 3.2, h: 0.35, fontSize: 10, color: M, bold: true });
                s2.addText(String(v), { x: 0.7 + col * 4, y: 1.7 + row * 1.5, w: 3.2, h: 0.5, fontSize: 28, color: W, bold: true });
            });

            await pptx.writeFile({ fileName: `NovantPro-${report.report_type}-${formatDate(report.period_start)}.pptx` });
            toast.success('PPTX exported');
        } catch (e) { toast.error('PPTX export failed'); }
        finally { setExporting(null); }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    if (!report) return null;

    const li = report.linkedin || {};
    const em = report.email || {};
    const ads = report.ads || {};
    const inb = report.inbound || {};
    const goals = report.goals || {};
    const profiles = li.profiles || [];

    const liAcceptRate = calculatePercentage(li.connection_accepted, li.reachouts_sent);
    const liResponseRate = calculatePercentage(li.responses, li.reachouts_sent);
    const emDeliveryRate = calculatePercentage(em.delivered, em.emails_sent);
    const emOpenRate = calculatePercentage(em.opened, em.delivered);
    const emClickRate = calculatePercentage(em.clicked, em.opened);

    return (
        <div className="min-h-screen" data-testid="report-detail-page">
            <Header
                title={`${report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)} Report`}
                subtitle={formatDateRange(report.period_start, report.period_end)}
            />

            <div className="p-8 space-y-8">
                {/* Top Bar */}
                <div className="flex items-center justify-between">
                    <Button variant="ghost" className="btn-ghost gap-2" onClick={() => navigate(-1)} data-testid="report-detail-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Back to Reports
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="ghost" className="btn-ghost gap-2" onClick={exportToPDF} disabled={!!exporting} data-testid="report-detail-pdf-btn">
                            {exporting === 'pdf' ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
                        </Button>
                        <Button variant="ghost" className="btn-ghost gap-2" onClick={exportToPPT} disabled={!!exporting} data-testid="report-detail-ppt-btn">
                            {exporting === 'ppt' ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Presentation className="w-4 h-4" />} PPTX
                        </Button>
                    </div>
                </div>

                {/* Goal vs Actual (if goals exist) */}
                {(goals.target_mqls > 0 || goals.target_qualified_leads > 0 || goals.total_reachouts > 0) && (
                    <section className="card-base p-6" data-testid="report-detail-goals">
                        <h3 className="text-lg font-heading font-bold text-white mb-4">GOAL vs ACTUAL</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {goals.total_reachouts > 0 && <GoalCard label="Reachouts" actual={report.metrics.total_reachouts} goal={goals.total_reachouts} />}
                            {goals.responses > 0 && <GoalCard label="Responses" actual={report.metrics.responses} goal={goals.responses} />}
                            {goals.target_mqls > 0 && <GoalCard label="MQLs" actual={report.metrics.mqls} goal={goals.target_mqls} />}
                            {goals.target_qualified_leads > 0 && <GoalCard label="SQLs" actual={report.metrics.qualified_leads} goal={goals.target_qualified_leads} />}
                            {goals.target_accounts > 0 && <GoalCard label="Target Accounts" actual={report.metrics.target_accounts} goal={goals.target_accounts} />}
                            {goals.total_contacts > 0 && <GoalCard label="Contacts" actual={report.metrics.total_contacts} goal={goals.total_contacts} />}
                        </div>
                    </section>
                )}

                {/* Channel Tabs */}
                <Tabs defaultValue="linkedin" className="w-full">
                    <TabsList className="bg-surface-card">
                        <TabsTrigger value="linkedin" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white gap-1.5" data-testid="tab-linkedin">
                            <Linkedin className="w-4 h-4" /> LinkedIn
                        </TabsTrigger>
                        <TabsTrigger value="email" className="data-[state=active]:bg-channel-email data-[state=active]:text-white gap-1.5" data-testid="tab-email">
                            <Mail className="w-4 h-4" /> Email
                        </TabsTrigger>
                        <TabsTrigger value="ads" className="data-[state=active]:bg-channel-ads data-[state=active]:text-white gap-1.5" data-testid="tab-ads">
                            <Megaphone className="w-4 h-4" /> Ads
                        </TabsTrigger>
                        <TabsTrigger value="inbound" className="data-[state=active]:bg-channel-inbound data-[state=active]:text-white gap-1.5" data-testid="tab-inbound">
                            <TrendingUp className="w-4 h-4" /> Inbound
                        </TabsTrigger>
                    </TabsList>

                    {/* LinkedIn Tab */}
                    <TabsContent value="linkedin" className="mt-6 space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <KPICard title="Target Accounts" value={li.target_accounts || 0} icon={Target} color="linkedin" />
                            <KPICard title="Target Contacts" value={li.target_contacts || 0} icon={Users} color="linkedin" />
                            <KPICard title="Reachouts" value={li.reachouts_sent || 0} icon={Send} />
                            <KPICard title="Accepted" value={li.connection_accepted || 0} icon={UserCheck} />
                            <KPICard title="Responses" value={li.responses || 0} icon={MessageSquare} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Positive" value={li.positive_responses || 0} icon={ThumbsUp} color="inbound" />
                            <KPICard title="Rejections" value={li.rejections || 0} icon={ThumbsDown} />
                            <KPICard title="MQLs" value={li.mqls || 0} icon={TrendingUp} color="red" />
                            <KPICard title="SQLs" value={li.qualified_leads || 0} icon={Award} color="inbound" />
                        </div>

                        {/* Conversion rates */}
                        <div className="card-base p-6">
                            <h4 className="text-sm font-heading font-bold text-white mb-4">CONVERSION RATES</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                <RateBar label="Acceptance" value={liAcceptRate} color="#0077B5" />
                                <RateBar label="Response" value={liResponseRate} color="#DC2626" />
                                <RateBar label="Positive" value={calculatePercentage(li.positive_responses, li.responses)} color="#10B981" />
                                <RateBar label="MQL" value={calculatePercentage(li.mqls, li.reachouts_sent)} color="#DC2626" scale={5} />
                            </div>
                        </div>

                        {/* Profiles */}
                        {profiles.length > 0 && (
                            <div className="card-base p-6">
                                <h4 className="text-sm font-heading font-bold text-white mb-4">PROFILE PERFORMANCE ({profiles.length})</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {profiles.map((p, i) => (
                                        <div key={p.profile_name || `profile-${i}`} className="p-4 rounded-lg bg-surface-app border border-line-default" data-testid={`report-profile-${i}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <Linkedin className="w-5 h-5 text-channel-linkedin" />
                                                <span className="font-heading font-bold text-white">{p.profile_name}</span>
                                            </div>
                                            {(p.target_industries?.length > 0 || p.target_company_sizes?.length > 0 || p.target_designations?.length > 0) && (
                                                <div className="mb-3 space-y-1">
                                                    {p.target_industries?.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <Building className="w-3 h-3 text-channel-linkedin shrink-0" />
                                                            {p.target_industries.map((ind) => <Badge key={ind} className="text-[10px] bg-channel-linkedin/10 text-channel-linkedin border-0">{ind}</Badge>)}
                                                        </div>
                                                    )}
                                                    {p.target_company_sizes?.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <Users className="w-3 h-3 text-channel-ads shrink-0" />
                                                            {p.target_company_sizes.map((sz) => <Badge key={sz} className="text-[10px] bg-channel-ads/10 text-channel-ads border-0">{sz}</Badge>)}
                                                        </div>
                                                    )}
                                                    {p.target_designations?.length > 0 && (
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <Briefcase className="w-3 h-3 text-brand-red shrink-0" />
                                                            {p.target_designations.map((d) => <Badge key={d} className="text-[10px] bg-brand-red/10 text-brand-red border-0">{d}</Badge>)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="grid grid-cols-4 gap-2 text-center">
                                                <div><p className="text-lg font-bold text-white">{p.reachouts_sent}</p><p className="text-[10px] text-content-muted">Reach</p></div>
                                                <div><p className="text-lg font-bold text-channel-linkedin">{p.connection_accepted}</p><p className="text-[10px] text-content-muted">Accept</p></div>
                                                <div><p className="text-lg font-bold text-brand-red">{p.mqls}</p><p className="text-[10px] text-content-muted">MQLs</p></div>
                                                <div><p className="text-lg font-bold text-status-success">{p.qualified_leads}</p><p className="text-[10px] text-content-muted">SQLs</p></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Breakdowns */}
                        {(Object.keys(li.industry_breakdown || {}).length > 0 || Object.keys(li.persona_breakdown || {}).length > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {Object.keys(li.industry_breakdown || {}).length > 0 && (
                                    <PieChartComponent data={Object.entries(li.industry_breakdown).map(([n, v]) => ({ name: n, value: v }))} title="INDUSTRY BREAKDOWN" />
                                )}
                                {Object.keys(li.persona_breakdown || {}).length > 0 && (
                                    <PieChartComponent data={Object.entries(li.persona_breakdown).map(([n, v]) => ({ name: n, value: v }))} title="PERSONA BREAKDOWN" />
                                )}
                            </div>
                        )}
                    </TabsContent>

                    {/* Email Tab */}
                    <TabsContent value="email" className="mt-6 space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Sent" value={em.emails_sent || 0} icon={Send} color="email" />
                            <KPICard title="Delivered" value={em.delivered || 0} icon={Inbox} />
                            <KPICard title="Opened" value={em.opened || 0} icon={Eye} />
                            <KPICard title="Clicked" value={em.clicked || 0} icon={MousePointer} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Replies" value={em.replies || 0} icon={MessageSquare} />
                            <KPICard title="Positive Replies" value={em.positive_replies || 0} icon={ThumbsUp} color="inbound" />
                            <KPICard title="MQLs" value={em.mqls || 0} icon={TrendingUp} color="red" />
                            <KPICard title="SQLs" value={em.qualified_leads || 0} icon={Award} color="inbound" />
                        </div>
                        <div className="card-base p-6">
                            <h4 className="text-sm font-heading font-bold text-white mb-4">FUNNEL RATES</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                <RateBar label="Delivery" value={emDeliveryRate} color="#10B981" />
                                <RateBar label="Open" value={emOpenRate} color="#EA4335" />
                                <RateBar label="Click" value={emClickRate} color="#DC2626" />
                                <RateBar label="Reply" value={calculatePercentage(em.replies, em.delivered)} color="#DC2626" scale={3} />
                            </div>
                        </div>
                        {(Object.keys(em.industry_heatmap || {}).length > 0 || Object.keys(em.geography_heatmap || {}).length > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {Object.keys(em.industry_heatmap || {}).length > 0 && <PieChartComponent data={Object.entries(em.industry_heatmap).map(([n, v]) => ({ name: n, value: v }))} title="INDUSTRY HEATMAP" />}
                                {Object.keys(em.geography_heatmap || {}).length > 0 && <BarChartComponent data={Object.entries(em.geography_heatmap).map(([n, v]) => ({ name: n, opens: v }))} dataKeys={['opens']} title="GEOGRAPHY HEATMAP" />}
                            </div>
                        )}
                    </TabsContent>

                    {/* Ads Tab */}
                    <TabsContent value="ads" className="mt-6 space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Accounts Targeted" value={ads.accounts_targeted || 0} icon={Target} color="ads" />
                            <KPICard title="Impressions" value={ads.impressions || 0} icon={Eye} />
                            <KPICard title="Clicks" value={ads.clicks || 0} icon={MousePointer} />
                            <KPICard title="CTR" value={`${(ads.ctr || 0).toFixed(2)}%`} icon={Percent} color="red" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Account Reach" value={`${(ads.account_reach_percent || 0).toFixed(1)}%`} icon={Users} />
                            <KPICard title="Engagement Rate" value={`${(ads.engagement_rate || 0).toFixed(1)}%`} icon={TrendingUp} />
                            <KPICard title="Cost/Engaged" value={formatCurrency(ads.cost_per_engaged_account || 0)} icon={DollarSign} />
                            <KPICard title="Cost/MQL" value={formatCurrency(ads.cost_per_mql || 0)} icon={DollarSign} color="red" />
                        </div>
                        <div className="card-base p-6">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-heading font-bold text-white">INFLUENCED PIPELINE</h4>
                                <span className="text-2xl font-heading font-bold text-brand-red">{formatCurrency(ads.influenced_pipeline || 0)}</span>
                            </div>
                            <div className="h-3 bg-surface-app rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-brand-red to-channel-ads" style={{ width: `${ads.account_reach_percent || 0}%` }} />
                            </div>
                        </div>
                        {(Object.keys(ads.industry_breakdown || {}).length > 0 || Object.keys(ads.tier_breakdown || {}).length > 0) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {Object.keys(ads.industry_breakdown || {}).length > 0 && <PieChartComponent data={Object.entries(ads.industry_breakdown).map(([n, v]) => ({ name: n, value: v }))} title="INDUSTRY DISTRIBUTION" />}
                                {Object.keys(ads.tier_breakdown || {}).length > 0 && <BarChartComponent data={Object.entries(ads.tier_breakdown).map(([n, v]) => ({ name: n, accounts: v }))} dataKeys={['accounts']} title="TIER BREAKDOWN" />}
                            </div>
                        )}
                    </TabsContent>

                    {/* Inbound Tab */}
                    <TabsContent value="inbound" className="mt-6 space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Total Views" value={inb.total_views || 0} icon={Eye} color="inbound" />
                            <KPICard title="Engagement" value={inb.total_engagement || 0} icon={Heart} />
                            <KPICard title="Leads" value={inb.total_leads || 0} icon={Users} color="red" />
                            <KPICard title="Content Pieces" value={(inb.contents || []).length} icon={FileText} />
                        </div>
                        {(inb.contents || []).length > 0 && (
                            <div className="card-base p-6">
                                <h4 className="text-sm font-heading font-bold text-white mb-4">CONTENT ASSETS</h4>
                                <div className="space-y-3">
                                    {inb.contents.map((c) => (
                                        <div key={c.title || c.type} className="flex items-center justify-between p-3 rounded-lg bg-surface-app border border-line-default">
                                            <div>
                                                <p className="font-medium text-white">{c.title}</p>
                                                <Badge className="text-[10px] mt-1">{c.type?.replace('_', ' ')}</Badge>
                                            </div>
                                            <div className="flex gap-4 text-sm">
                                                <span className="text-content-muted"><Eye className="w-3 h-3 inline mr-1" />{formatNumber(c.views)}</span>
                                                <span className="text-content-muted"><Heart className="w-3 h-3 inline mr-1" />{c.likes}</span>
                                                <span className="text-brand-red font-bold"><Users className="w-3 h-3 inline mr-1" />{c.leads_generated}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Insights & Recommendations */}
                {((report.insights || []).length > 0 || (report.recommendations || []).length > 0) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {report.insights?.length > 0 && (
                            <div className="card-base p-6" data-testid="report-detail-insights">
                                <h3 className="text-sm font-heading font-bold text-white mb-4 flex items-center gap-2">
                                    <Lightbulb className="w-4 h-4 text-channel-ads" /> INSIGHTS & OBSERVATIONS
                                </h3>
                                <ul className="space-y-2">
                                    {report.insights.map((ins, i) => (
                                        <li key={`insight-${i}-${ins.slice(0,20)}`} className="flex items-start gap-2 text-sm text-content-secondary">
                                            <span className="text-brand-red mt-0.5">•</span> {ins}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {report.recommendations?.length > 0 && (
                            <div className="card-base p-6" data-testid="report-detail-recommendations">
                                <h3 className="text-sm font-heading font-bold text-white mb-4 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-status-success" /> RECOMMENDATIONS
                                </h3>
                                <ul className="space-y-2">
                                    {report.recommendations.map((rec, i) => (
                                        <li key={`rec-${i}-${rec.slice(0,20)}`} className="flex items-start gap-2 text-sm text-status-success/80">
                                            <span className="text-status-success mt-0.5">•</span> {rec}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function GoalCard({ label, actual, goal }) {
    const pct = goal > 0 ? Math.min((actual / goal) * 100, 100) : 0;
    const over = actual >= goal;
    return (
        <div className="p-4 rounded-lg bg-surface-app border border-line-default">
            <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-content-muted uppercase">{label}</span>
                <span className={`text-xs font-bold ${over ? 'text-status-success' : 'text-brand-red'}`}>{pct.toFixed(0)}%</span>
            </div>
            <div className="flex items-end gap-2 mb-2">
                <span className="text-2xl font-bold text-white">{actual}</span>
                <span className="text-sm text-content-muted">/ {goal}</span>
            </div>
            <div className="h-1.5 bg-surface-card rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${over ? 'bg-status-success' : 'bg-brand-red'}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function RateBar({ label, value, color, scale = 1 }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-sm text-content-secondary">{label}</span>
                <span className="text-lg font-bold" style={{ color }}>{value}%</span>
            </div>
            <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width: `${Math.min(value * scale, 100)}%`, background: color }} />
            </div>
        </div>
    );
}
